/**
 * @name global.ts
 * @version 1.0.0
 * @description Módulo de estado global para Vuex
 * @author
 * @changelog
 * 1.0.0 - 2024/01/01 - @usuario - Cambio inicial
 */

import { Module } from 'vuex'
import { RootState } from '../types'
import { getAuth } from 'firebase/auth'
import { ref, get, update } from 'firebase/database'
import { database } from '@/firebase/config'
import { GlobalState } from '@/store/types'
import { EGender } from '@shared/types'

interface ActionContext {
	commit: (type: string, payload?: any) => void
	dispatch: (type: string, payload?: any) => Promise<any>
	state: GlobalState
}

const globalModule: Module<GlobalState, RootState> = {
	namespaced: true,

	state: {
		testMode: false,
		user: {
			me: {
				userData: {
					institution: '',
					testMode: false,
					base64Cer: ''
				},
				role: undefined,
				institutions: {},
				cer: {
					certificadoResponsable: '',
					noCertificadoResponsable: '',
					pem: '',
					x509: {
						extensions: {
							basicConstraints: {
								cA: false,
								critical: false,
								id: '',
								name: '',
								value: ''
							},
							extKeyUsage: {
								clientAuth: false,
								critical: false,
								emailProtection: false,
								id: '',
								name: '',
								value: ''
							},
							keyUsage: {
								cRLSign: false,
								critical: false,
								dataEncipherment: false,
								decipherOnly: false,
								digitalSignature: false,
								encipherOnly: false,
								id: '',
								keyAgreement: false,
								keyCertSign: false,
								keyEncipherment: false,
								name: '',
								nonRepudiation: false,
								value: ''
							},
							nsCertType: {
								client: false,
								critical: false,
								email: false,
								emailCA: false,
								id: '',
								name: '',
								objCA: false,
								objsign: false,
								reserved: false,
								server: false,
								sslCA: false,
								value: ''
							}
						},
						fingerPrint: '',
						issuer: [],
						notAfter: '',
						notBefore: '',
						publicKey: {
							bitSize: 0,
							fingerPrint: '',
							pem: ''
						},
						serial: '',
						siginfo: {
							algorithmOid: ''
						},
						signature: '',
						subject: {
							'2-5-4-41': '',
							'2-5-4-45': '',
							commonName: '',
							countryName: '',
							emailAddress: '',
							organizationName: '',
							serialNumber: ''
						},
						subjectHash: '',
						version: 0
					}
				},
				curp: '',
				email: '',
				nombre: '',
				primerApellido: '',
				segundoApellido: '',
				phone: '',
				genere: EGender.MALE
			}
		},
		institutions: {},
		selectedInstitution: {
			CCT: '',
			hasCertisep: false,
			Carrera: {},
			certisep: {},
			Campus: '',
			idNombreInstitucion: '',
			cveInstitution: '',
			Responsable: {},
			idCampus: {},
			nombreInstitucion: '',
			logo128: ''
		},
		cveCarreraToIdCarrera: {}
	},

	getters: {
		currentInstitution: (state: GlobalState) => state.user.me.userData.institution,
		isTestMode: (state: GlobalState) => state.testMode,
		institutionName: (state: GlobalState) => (id: string) => state.institutions[id] || '',
		hasValidInstitution: (state: GlobalState) => Boolean(state.selectedInstitution.Carrera),
		careers: (state: GlobalState) => state.selectedInstitution.Carrera,
		careerIdByKey: (state: GlobalState) => (key: string) => state.cveCarreraToIdCarrera[key],
		selectedInstitution: (state: GlobalState) => state.selectedInstitution,
		userCER: (state: GlobalState) => state.user.me.cer
	},

	mutations: {
		SET_TEST_MODE(state: GlobalState, value: boolean) {
			state.testMode = value
			state.user.me.userData.testMode = value
		},

		SET_USER_INSTITUTION(state: GlobalState, institutionId: string) {
			state.user.me.userData.institution = institutionId
		},

		SET_USER_INSTITUTIONS(state: GlobalState, institutions: Record<string, string>) {
			state.user.me.institutions = institutions
		},

		SET_INSTITUTIONS(state: GlobalState, institutions: Record<string, string>) {
			state.institutions = institutions
		},

		SET_SELECTED_INSTITUTION(state: GlobalState, data: Partial<GlobalState['selectedInstitution']>) {
			state.selectedInstitution = { ...state.selectedInstitution, ...data }
		},

		SET_CAREER_MAPPING(state: GlobalState, mapping: Record<string, string>) {
			state.cveCarreraToIdCarrera = mapping
		},

		SET_USER_CER(state: GlobalState, cer: any) {
			state.user.me.cer = cer
		},

		SET_USER_CURP(state: GlobalState, curp: string) {
			state.user.me.curp = curp
		},

		SET_USER_EMAIL(state: GlobalState, email: string) {
			state.user.me.email = email
		},

		SET_USER_PHONE(state: GlobalState, phone: string) {
			state.user.me.phone = phone
		},

		SET_USER_SEGUNDO_APELLIDO(state: GlobalState, segundoApellido: string) {
			state.user.me.segundoApellido = segundoApellido
		},

		SET_USER_NOMBRE(state: GlobalState, nombre: string) {
			state.user.me.nombre = nombre
		},

		SET_USER_PRIMER_APELLIDO(state: GlobalState, primerApellido: string) {
			state.user.me.primerApellido = primerApellido
		},
		SET_USER_NAME(state: GlobalState, name: string) {
			state.user.me.nombre = name
		},
		SET_USER_DATA(state: GlobalState, userData: any) {
			state.user.me.userData = { ...state.user.me.userData, ...userData }
		},

		SET_USER_PROFILE(state: GlobalState, profile: any) {
			if (profile === null) {
				state.user.me = {
					userData: {
						institution: '',
						testMode: false,
						base64Cer: ''
					},
					role: undefined,
					institutions: {},
					cer: {
						certificadoResponsable: '',
						noCertificadoResponsable: '',
						pem: '',
						x509: {
							extensions: {
								basicConstraints: { cA: false, critical: false, id: '', name: '', value: '' },
								extKeyUsage: { clientAuth: false, critical: false, emailProtection: false, id: '', name: '', value: '' },
								keyUsage: {
									cRLSign: false,
									critical: false,
									dataEncipherment: false,
									decipherOnly: false,
									digitalSignature: false,
									encipherOnly: false,
									id: '',
									keyAgreement: false,
									keyCertSign: false,
									keyEncipherment: false,
									name: '',
									nonRepudiation: false,
									value: ''
								},
								nsCertType: { client: false, critical: false, email: false, emailCA: false, id: '', name: '', objCA: false, objsign: false, reserved: false, server: false, sslCA: false, value: '' }
							},
							fingerPrint: '',
							issuer: [],
							notAfter: '',
							notBefore: '',
							publicKey: { bitSize: 0, fingerPrint: '', pem: '' },
							serial: '',
							siginfo: { algorithmOid: '' },
							signature: '',
							subject: { '2-5-4-41': '', '2-5-4-45': '', commonName: '', countryName: '', emailAddress: '', organizationName: '', serialNumber: '' },
							subjectHash: '',
							version: 0
						}
					},
					curp: '',
					email: '',
					nombre: '',
					primerApellido: '',
					segundoApellido: '',
					phone: '',
					genere: EGender.MALE
				}
			} else {
				state.user.me = { ...state.user.me, ...profile }
			}
		}
	},

	actions: {
		async changeTestMode({ commit }: ActionContext, value: boolean) {
			try {
				const auth = getAuth()
				const uid = auth.currentUser?.uid

				if (!uid) throw new Error('No user logged in')
				console.log('path', `users/${uid}/userData`)
				await update(ref(database, `users/${uid}/userData`), {
					testMode: value
				})

				commit('SET_TEST_MODE', value)
			} catch (error) {
				console.error('Error changing test mode:', error)
				throw error
			}
		},

		async loadInstitutions({ commit, state, dispatch }: ActionContext) {
			try {
				const auth = getAuth()
				const uid = auth.currentUser?.uid

				if (!uid) throw new Error('No user logged in')

				// Get user's institutions
				const userSnapshot = await get(ref(database, `users/${uid}`))
				const userData = userSnapshot.val()

				if (!userData?.institutions) {
					// TODO: Show institution request popup
					return
				}

				// Set user's institutions in state
				commit('SET_USER_INSTITUTIONS', userData.institutions)

				// Get institution names
				const institutions: Record<string, string> = {}
				const promises = Object.keys(userData.institutions).map(async institutionId => {
					const [nameSnapshot, campusSnapshot] = await Promise.all([
						get(ref(database, `institutions/${institutionId}/nombreInstitucion`)),
						get(ref(database, `institutions/${institutionId}/campusName`))
					])

					const name = nameSnapshot.val() || 'No se pudo recuperar el nombre de la institución'
					const campus = campusSnapshot.val()

					institutions[institutionId] = campus ? `${name}, campus: ${campus}` : name
				})

				await Promise.all(promises)
				commit('SET_INSTITUTIONS', institutions)

				// Set initial institution if none selected
				if (!state.user.me.userData.institution) {
					const firstInstitution = Object.keys(userData.institutions)[0]
					await dispatch('changeInstitution', firstInstitution)
				} else {
					await dispatch('loadInstitutionData', state.user.me.userData.institution)
				}
			} catch (error) {
				console.error('Error loading institutions:', error)
				throw error
			}
		},

		async changeInstitution({ commit, dispatch }: ActionContext, institutionId: string) {
			try {
				const auth = getAuth()
				const uid = auth.currentUser?.uid

				if (!uid) throw new Error('No user logged in')

				await update(ref(database, `users/${uid}/userData`), {
					institution: institutionId
				})

				commit('SET_USER_INSTITUTION', institutionId)
				await dispatch('loadInstitutionData', institutionId)
			} catch (error) {
				console.error('Error changing institution:', error)
				throw error
			}
		},

		async loadInstitutionData({ commit }: ActionContext, institutionId: string) {
			try {
				const [
					careersSnapshot,
					certisepSnapshot,
					campusSnapshot,
					idNombreSnapshot,
					cveInstitutionSnapshot,
					responsableSnapshot,
					idCampusSnapshot,
					nombreInstitucionSnapshot,
					logo128Snapshot,
					CCTSnapshot,
					hasCertisepSnapshot
				] = await Promise.all([
					get(ref(database, `institutions/${institutionId}/Carrera`)),
					get(ref(database, `institutions/${institutionId}/certisep`)),
					get(ref(database, `institutions/${institutionId}/idCampus`)),
					get(ref(database, `institutions/${institutionId}/idNombreInstitucion`)),
					get(ref(database, `institutions/${institutionId}/cveInstitution`)),
					get(ref(database, `institutions/${institutionId}/Responsable`)),
					get(ref(database, `institutions/${institutionId}/idCampus`)),
					get(ref(database, `institutions/${institutionId}/nombreInstitucion`)),
					get(ref(database, `institutions/${institutionId}/logo128`)),
					get(ref(database, `institutions/${institutionId}/CCT`)),
					get(ref(database, `institutions/${institutionId}/hasCertisep`))
				])

				const careers = Object.values(careersSnapshot.val() || {})
				const cveCarreraToIdCarrera: Record<string, string> = {}

				Object.entries(careers).forEach(([id, career]: [string, any]) => {
					cveCarreraToIdCarrera[career.claveCarrera] = id
				})

				commit('SET_SELECTED_INSTITUTION', {
					Carrera: careers,
					certisep: certisepSnapshot.val() || {},
					Campus: campusSnapshot.val() || '',
					idNombreInstitucion: idNombreSnapshot.val() || '',
					cveInstitution: cveInstitutionSnapshot.val() || '',
					Responsable: responsableSnapshot.val() || {},
					idCampus: idCampusSnapshot.val() || {},
					nombreInstitucion: nombreInstitucionSnapshot.val() || '',
					logo128: logo128Snapshot.val() || '',
					CCT: CCTSnapshot.val() || '',
					hasCertisep: hasCertisepSnapshot.val() ?? false
				})

				commit('SET_CAREER_MAPPING', cveCarreraToIdCarrera)
			} catch (error) {
				console.error('Error loading institution data:', error)
				throw error
			}
		},

		async loadUserData({ commit, state, dispatch }: ActionContext) {
			try {
				const auth = getAuth()
				const uid = auth.currentUser?.uid

				if (!uid) throw new Error('No user logged in')

				// Cargar datos del usuario
				const userSnapshot = await get(ref(database, `users/${uid}`))
				const userData = userSnapshot.val()
				const getSplitName = (name: string) => {
					const nameSplit = name.split(' ')
					return {
						nombre: nameSplit[0],
						primerApellido: nameSplit[1],
						segundoApellido: nameSplit[2]
					}
				}
				if (!userData) throw new Error('No user data found')

				// Actualizar perfil completo del usuario
				commit('SET_USER_PROFILE', {
					userData: {
						...state.user.me.userData,
						...userData.userData
					},
					role: {
						tipoRole: userData.role?.tipoRole,
						descriptionRole: userData.role?.descriptionRole
					},
					institutions: userData.institutions || {},
					cer: userData.cer || state.user.me.cer,
					curp: userData.curp || state.user.me.curp,
					email: userData.email || state.user.me.email,
					nombre: getSplitName(userData.name || state.user.me.nombre).nombre,
					primerApellido: getSplitName(userData.name || state.user.me.primerApellido).primerApellido,
					segundoApellido: getSplitName(userData.name || state.user.me.segundoApellido).segundoApellido,
					phone: userData.phone || state.user.me.phone,
					genere: userData.genere || state.user.me.genere
				})

				// Si hay una institución seleccionada, cargar sus datos
				if (userData.userData?.institution) {
					await dispatch('loadInstitutionData', userData.userData.institution)
				}

				// Cargar nombres de todas las instituciones (solo IDs válidos; tolerar Permission denied por institución)
				const institutions: Record<string, string> = {}
				const institutionIds = Object.keys(userData.institutions || {}).filter(id => id != null && String(id).trim() !== '')
				const results = await Promise.allSettled(
					institutionIds.map(async instId => {
						const [nameSnapshot, campusSnapshot] = await Promise.all([get(ref(database, `institutions/${instId}/nombreInstitucion`)), get(ref(database, `institutions/${instId}/campusName`))])
						const name = nameSnapshot.val() || 'No se pudo recuperar el nombre de la institución'
						const campus = campusSnapshot.val()
						return { instId, label: campus ? `${name}, campus: ${campus}` : name }
					})
				)
				for (const result of results) {
					if (result.status === 'fulfilled') {
						institutions[result.value.instId] = result.value.label
					}
				}
				commit('SET_INSTITUTIONS', institutions)
			} catch (error) {
				console.error('Error loading user data:', error)
				throw error
			}
		},

		async loadInstitution({ commit, state }: ActionContext, institutionId: string) {
			try {
				const auth = getAuth()
				const uid = auth.currentUser?.uid

				if (!uid) throw new Error('No user logged in')

				// Cargar datos del usuario
				const userSnapshot = await get(ref(database, `users/${uid}`))
				const userData = userSnapshot.val()

				if (!userData) throw new Error('No user data found')

				// Actualizar perfil completo del usuario
				commit('SET_USER_PROFILE', {
					userData: {
						...state.user.me.userData,
						...userData.userData
					},
					institutions: userData.institutions || {},
					cer: userData.cer || state.user.me.cer
				})

				// Cargar datos de la institución
				const [careersSnapshot, certisepSnapshot, campusSnapshot, idNombreSnapshot, cveInstitutionSnapshot, responsableSnapshot, idCampusSnapshot, CCTSnapshot, hasCertisepSnapshot] =
					await Promise.all([
						get(ref(database, `institutions/${institutionId}/Carrera`)),
						get(ref(database, `institutions/${institutionId}/certisep`)),
						get(ref(database, `institutions/${institutionId}/idCampus`)),
						get(ref(database, `institutions/${institutionId}/idNombreInstitucion`)),
						get(ref(database, `institutions/${institutionId}/cveInstitution`)),
						get(ref(database, `institutions/${institutionId}/Responsable`)),
						get(ref(database, `institutions/${institutionId}/idCampus`)),
						get(ref(database, `institutions/${institutionId}/CCT`)),
						get(ref(database, `institutions/${institutionId}/hasCertisep`))
					])

				const careers = careersSnapshot.val() || {}
				const cveCarreraToIdCarrera: Record<string, string> = {}

				Object.entries(careers).forEach(([id, career]: [string, any]) => {
					cveCarreraToIdCarrera[career.claveCarrera] = id
				})
				// Actualizar datos de la institución seleccionada
				commit('SET_SELECTED_INSTITUTION', {
					CCT: CCTSnapshot.val() || '',
					hasCertisep: hasCertisepSnapshot.val() ?? false,
					Carrera: careers,
					certisep: certisepSnapshot.val() || {},
					Campus: campusSnapshot.val() || '',
					idNombreInstitucion: idNombreSnapshot.val() || '',
					cveInstitution: cveInstitutionSnapshot.val() || '',
					Responsable: responsableSnapshot.val() || {},
					idCampus: idCampusSnapshot.val() || {}
				})

				commit('SET_CAREER_MAPPING', cveCarreraToIdCarrera)

				// Cargar nombres de todas las instituciones (solo IDs válidos; tolerar Permission denied por institución)
				const institutions: Record<string, string> = {}
				const institutionIds = Object.keys(userData.institutions || {}).filter(id => id != null && String(id).trim() !== '')
				const results = await Promise.allSettled(
					institutionIds.map(async instId => {
						const [nameSnapshot, campusSnapshot] = await Promise.all([get(ref(database, `institutions/${instId}/nombreInstitucion`)), get(ref(database, `institutions/${instId}/campusName`))])
						const name = nameSnapshot.val() || 'No se pudo recuperar el nombre de la institución'
						const campus = campusSnapshot.val()
						return { instId, label: campus ? `${name}, campus: ${campus}` : name }
					})
				)
				for (const result of results) {
					if (result.status === 'fulfilled') {
						institutions[result.value.instId] = result.value.label
					}
				}
				commit('SET_INSTITUTIONS', institutions)
			} catch (error) {
				console.error('Error loading institution data:', error)
				throw error
			}
		}
	}
}

export default globalModule
